import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Badge, Button, Spinner, ProgressBar,
  Form, ButtonGroup, Alert, OverlayTrigger, Tooltip, Modal
} from 'react-bootstrap';
import {
  FaProjectDiagram, FaList, FaThLarge, FaSearch, FaFilter,
  FaChartBar, FaHeart, FaSortAmountDown, FaSortAmountUp,
  FaLayerGroup, FaSyncAlt, FaChevronRight, FaInfoCircle,
  FaTimes, FaExchangeAlt, FaSitemap, FaThermometerHalf,
  FaShieldAlt, FaStar, FaEye, FaClock, FaDatabase
} from 'react-icons/fa';
import knowledgeBaseApi from '../../services/knowledgeBaseApi';
import ClusterDetailView from './ClusterDetailView';

// ── Helpers ──
const scoreColor = (val) => {
  const pct = val * 100;
  if (pct >= 75) return '#22c55e';
  if (pct >= 50) return '#eab308';
  if (pct >= 25) return '#f97316';
  return '#ef4444';
};

const formatNum = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return String(n);
};

// ── Stat Card ──
const StatCard = ({ label, value, icon: Icon, color, subtitle }) => (
  <div className="cm-stat-card">
    <div className="cm-stat-icon" style={{ color: color || 'var(--electric-cyan)' }}>
      <Icon />
    </div>
    <div className="cm-stat-info">
      <div className="cm-stat-value">{value}</div>
      <div className="cm-stat-label">{label}</div>
      {subtitle && <div className="cm-stat-subtitle">{subtitle}</div>}
    </div>
  </div>
);

// ── Category Pill ──
const CategoryPill = ({ cat, active, onClick }) => (
  <button
    className={`cm-cat-pill ${active ? 'active' : ''}`}
    onClick={onClick}
  >
    {cat.category || 'Uncategorized'}
    <span className="cm-cat-count">{cat.cluster_count}</span>
  </button>
);

// ── Cluster Card (Grid) ──
const ClusterGridCard = ({ cluster, onSelect, onCompare, compareMode }) => {
  const coherence = cluster.coherence_score ?? cluster.cluster_coherence_score ?? 0;
  const stability = cluster.stability_score ?? cluster.cluster_stability_score ?? 0;
  const quality = cluster.avg_quality ?? cluster.avg_member_quality ?? 0;
  const cColor = scoreColor(coherence);

  return (
    <Card
      className="cm-cluster-card cm-cluster-card-grid"
      onClick={() => !compareMode && onSelect(cluster.cluster_id)}
    >
      {/* Top color bar */}
      <div className="cm-card-accent" style={{ background: cColor }} />

      <Card.Body className="cm-card-body">
        <div className="cm-card-top">
          <div className="cm-card-label" title={cluster.cluster_label}>
            {cluster.cluster_label || 'Unnamed Cluster'}
          </div>
          {compareMode && (
            <Form.Check
              type="checkbox"
              className="cm-compare-check"
              onClick={(e) => { e.stopPropagation(); onCompare(cluster.cluster_id); }}
            />
          )}
        </div>

        <p className="cm-card-desc">
          {cluster.cluster_description || 'No description available'}
        </p>

        {/* Metrics row */}
        <div className="cm-card-metrics">
          <OverlayTrigger placement="top" overlay={<Tooltip>Members</Tooltip>}>
            <div className="cm-metric">
              <FaLayerGroup />
              <span>{cluster.member_count || 0}</span>
            </div>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Coherence: {(coherence * 100).toFixed(0)}%</Tooltip>}>
            <div className="cm-metric">
              <FaThermometerHalf style={{ color: cColor }} />
              <span style={{ color: cColor }}>{(coherence * 100).toFixed(0)}%</span>
            </div>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Stability: {(stability * 100).toFixed(0)}%</Tooltip>}>
            <div className="cm-metric">
              <FaShieldAlt />
              <span>{(stability * 100).toFixed(0)}%</span>
            </div>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Quality: {(quality * 100).toFixed(0)}%</Tooltip>}>
            <div className="cm-metric">
              <FaStar />
              <span>{(quality * 100).toFixed(0)}%</span>
            </div>
          </OverlayTrigger>
        </div>

        {/* Bottom bar */}
        <div className="cm-card-footer">
          <Badge className="cm-cat-badge">{cluster.cluster_category || 'general'}</Badge>
          {cluster.hierarchy_level > 0 && (
            <Badge className="cm-level-badge">L{cluster.hierarchy_level}</Badge>
          )}
          {cluster.evolution_count > 0 && (
            <span className="cm-evo-count">{cluster.evolution_count} evolutions</span>
          )}
        </div>

        {/* Coherence bar */}
        <div className="cm-coherence-track">
          <div className="cm-coherence-fill" style={{ width: `${coherence * 100}%`, background: cColor }} />
        </div>
      </Card.Body>
    </Card>
  );
};

// ── Cluster Row (List) ──
const ClusterListRow = ({ cluster, onSelect, onCompare, compareMode }) => {
  const coherence = cluster.coherence_score ?? cluster.cluster_coherence_score ?? 0;
  const stability = cluster.stability_score ?? cluster.cluster_stability_score ?? 0;
  const quality = cluster.avg_quality ?? cluster.avg_member_quality ?? 0;
  const cColor = scoreColor(coherence);

  return (
    <div className="cm-list-row" onClick={() => !compareMode && onSelect(cluster.cluster_id)}>
      {compareMode && (
        <Form.Check
          type="checkbox"
          className="cm-compare-check-list"
          onClick={(e) => { e.stopPropagation(); onCompare(cluster.cluster_id); }}
        />
      )}
      <div className="cm-list-color" style={{ background: cColor }} />
      <div className="cm-list-main">
        <div className="cm-list-label">{cluster.cluster_label || 'Unnamed'}</div>
        <div className="cm-list-desc">{cluster.cluster_description || 'No description'}</div>
      </div>
      <div className="cm-list-stats">
        <span className="cm-list-stat"><FaLayerGroup /> {cluster.member_count || 0}</span>
        <span className="cm-list-stat" style={{ color: cColor }}><FaThermometerHalf /> {(coherence * 100).toFixed(0)}%</span>
        <span className="cm-list-stat"><FaShieldAlt /> {(stability * 100).toFixed(0)}%</span>
        <span className="cm-list-stat"><FaStar /> {(quality * 100).toFixed(0)}%</span>
      </div>
      <Badge className="cm-cat-badge-sm">{cluster.cluster_category || 'general'}</Badge>
      <FaChevronRight className="cm-list-arrow" />
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════
const ClusterManager = ({ setError, setSuccess, selectedAgent }) => {
  // ── State ──
  const [view, setView] = useState('manager'); // manager | detail | compare | tree
  const [clusters, setClusters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [selectedClusterId, setSelectedClusterId] = useState(null);
  const [displayType, setDisplayType] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    sort_by: 'member_count',
    sort_direction: 'desc',
    cluster_category: '',
    min_member_count: '',
    min_coherence_score: '',
    page: 1,
    page_size: 50,
  });

  // Compare mode
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // Tree view
  const [treeData, setTreeData] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);

  const agentId = selectedAgent === 'all' ? undefined : parseInt(selectedAgent);

  // ── Fetch clusters ──
  const fetchClusters = useCallback(async () => {
    setLoading(true);
    try {
      const opts = { ...filters };
      if (opts.cluster_category === '') delete opts.cluster_category;
      if (opts.min_member_count === '') delete opts.min_member_count;
      else opts.min_member_count = parseInt(opts.min_member_count);
      if (opts.min_coherence_score === '') delete opts.min_coherence_score;
      else opts.min_coherence_score = parseFloat(opts.min_coherence_score);

      const data = await knowledgeBaseApi.clusters.list(agentId || 1, opts);
      setClusters(data?.clusters || []);
      setCategories(data?.categories || []);
      setPagination(data?.pagination || {});
    } catch (err) {
      console.error('Cluster fetch error:', err);
      setClusters([]);
    } finally {
      setLoading(false);
    }
  }, [agentId, filters]);

  // ── Fetch stats ──
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await knowledgeBaseApi.clusters.getStats(agentId);
      setStats(data?.stats || null);
    } catch (err) {
      console.error('Stats fetch error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [agentId]);

  // ── Effects ──
  useEffect(() => {
    fetchClusters();
    fetchStats();
  }, [fetchClusters, fetchStats]);

  // ── Search ──
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const data = await knowledgeBaseApi.clusters.search(searchTerm, agentId, 30);
      setSearchResults(data?.results || []);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults(null);
  };

  // ── Compare ──
  const toggleCompareId = (id) => {
    setCompareIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 10 ? [...prev, id] : prev
    );
  };

  const runCompare = async () => {
    if (compareIds.length < 2) {
      setError('Select at least 2 clusters to compare');
      return;
    }
    setCompareLoading(true);
    try {
      const data = await knowledgeBaseApi.clusters.compare(compareIds);
      setCompareResult(data);
      setView('compare');
    } catch (err) {
      setError('Compare failed: ' + err.message);
    } finally {
      setCompareLoading(false);
    }
  };

  // ── Tree ──
  const loadTree = async () => {
    setTreeLoading(true);
    try {
      const data = await knowledgeBaseApi.clusters.getFullTree(agentId || 1);
      setTreeData(data);
      setView('tree');
    } catch (err) {
      setError('Tree load failed: ' + err.message);
    } finally {
      setTreeLoading(false);
    }
  };

  // ── Select cluster → detail ──
  const selectCluster = (id) => {
    setSelectedClusterId(id);
    setView('detail');
  };

  // ── Back to manager ──
  const backToManager = () => {
    setView('manager');
    setSelectedClusterId(null);
    setCompareResult(null);
    setCompareIds([]);
    setCompareMode(false);
  };

  // ── Filter change ──
  const updateFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value, page: 1 }));
  };

  // ── Page change ──
  const changePage = (newPage) => {
    setFilters(f => ({ ...f, page: newPage }));
  };

  const displayedClusters = searchResults !== null ? searchResults : clusters;

  // ══════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ══════════════════════════════════════════════════════════
  if (view === 'detail' && selectedClusterId) {
    return (
      <ClusterDetailView
        clusterId={selectedClusterId}
        onBack={backToManager}
        setError={setError}
        setSuccess={setSuccess}
      />
    );
  }

  // ══════════════════════════════════════════════════════════
  // COMPARE VIEW
  // ══════════════════════════════════════════════════════════
  if (view === 'compare' && compareResult) {
    return (
      <div className="cm-container">
        <div className="cm-section-header">
          <Button variant="outline-secondary" size="sm" onClick={backToManager}>← Back</Button>
          <h6 className="cm-section-title"><FaExchangeAlt className="me-2" /> Cluster Comparison</h6>
        </div>

        {compareResult.shared_members !== null && compareResult.shared_members !== undefined && (
          <Alert variant="info" className="cm-alert">
            <strong>{compareResult.shared_members}</strong> shared members between these clusters
          </Alert>
        )}

        <div className="cm-compare-grid">
          {(compareResult.comparison || []).map((c, i) => (
            <Card key={c.cluster_id || i} className="cm-compare-card">
              <Card.Header className="cm-compare-header">
                <strong>{c.label || c.cluster_id?.slice(0, 8)}</strong>
                <Badge className="cm-cat-badge-sm">{c.category || 'general'}</Badge>
              </Card.Header>
              <Card.Body className="cm-compare-body">
                <div className="cm-compare-metrics">
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Members</span>
                    <span className="cm-compare-metric-value">{c.member_count}</span>
                  </div>
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Coherence</span>
                    <span className="cm-compare-metric-value" style={{ color: scoreColor(c.coherence) }}>
                      {(c.coherence * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Stability</span>
                    <span className="cm-compare-metric-value">{(c.stability * 100).toFixed(0)}%</span>
                  </div>
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Avg Quality</span>
                    <span className="cm-compare-metric-value">{(c.avg_quality * 100).toFixed(0)}%</span>
                  </div>
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Avg Trust</span>
                    <span className="cm-compare-metric-value">{(c.avg_trust * 100).toFixed(0)}%</span>
                  </div>
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Avg Credibility</span>
                    <span className="cm-compare-metric-value">{c.avg_credibility?.toFixed(3)}</span>
                  </div>
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Avg Importance</span>
                    <span className="cm-compare-metric-value">{c.avg_importance?.toFixed(3)}</span>
                  </div>
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Evolutions</span>
                    <span className="cm-compare-metric-value">{c.evolution_count}</span>
                  </div>
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Sources</span>
                    <span className="cm-compare-metric-value">{c.distinct_sources}</span>
                  </div>
                  <div className="cm-compare-metric">
                    <span className="cm-compare-metric-label">Neg Cred</span>
                    <span className="cm-compare-metric-value" style={{ color: c.negative_credibility > 0 ? '#ef4444' : undefined }}>
                      {c.negative_credibility}
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // TREE VIEW
  // ══════════════════════════════════════════════════════════
  if (view === 'tree' && treeData) {
    const nodes = treeData.tree || [];
    const roots = nodes.filter(n => !n.parent_id);
    const childrenOf = (pid) => nodes.filter(n => n.parent_id === pid);

    const renderTreeNode = (node, depth = 0) => (
      <div key={node.cluster_id} className="cm-tree-node" style={{ marginLeft: depth * 24 }}>
        <div
          className="cm-tree-node-row"
          onClick={() => selectCluster(node.cluster_id)}
        >
          <div className="cm-tree-indent">
            {depth > 0 && <span className="cm-tree-branch">└</span>}
          </div>
          <div className="cm-tree-color" style={{ background: scoreColor(node.coherence) }} />
          <span className="cm-tree-label">{node.label || 'Unnamed'}</span>
          <Badge className="cm-tree-badge">{node.members} items</Badge>
          <span className="cm-tree-coherence" style={{ color: scoreColor(node.coherence) }}>
            {(node.coherence * 100).toFixed(0)}%
          </span>
          {node.category && <Badge className="cm-cat-badge-sm">{node.category}</Badge>}
        </div>
        {childrenOf(node.cluster_id).map(c => renderTreeNode(c, depth + 1))}
      </div>
    );

    return (
      <div className="cm-container">
        <div className="cm-section-header">
          <Button variant="outline-secondary" size="sm" onClick={backToManager}>← Back</Button>
          <h6 className="cm-section-title"><FaSitemap className="me-2" /> Cluster Hierarchy Tree</h6>
          <span className="cm-tree-count">{nodes.length} clusters</span>
        </div>
        <div className="cm-tree-container">
          {roots.length > 0 ? roots.map(r => renderTreeNode(r)) : (
            nodes.map(n => renderTreeNode(n))
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // MANAGER VIEW (main)
  // ══════════════════════════════════════════════════════════
  return (
    <div className="cm-container">
      {/* ── Stats Bar ── */}
      {stats && (
        <div className="cm-stats-bar">
          <StatCard label="Active Clusters" value={stats.total_active_clusters || 0} icon={FaProjectDiagram} color="#3b82f6" />
          <StatCard label="Total Members" value={formatNum(stats.total_members)} icon={FaLayerGroup} color="#22c55e" />
          <StatCard label="Avg Coherence" value={`${((stats.avg_coherence || 0) * 100).toFixed(0)}%`} icon={FaThermometerHalf} color={scoreColor(stats.avg_coherence || 0)} />
          <StatCard label="Avg Quality" value={`${((stats.avg_quality || 0) * 100).toFixed(0)}%`} icon={FaStar} color="#eab308" />
          <StatCard label="Root Clusters" value={stats.root_clusters || 0} icon={FaSitemap} color="#8b5cf6" />
          <StatCard label="Empty" value={stats.empty_clusters || 0} icon={FaInfoCircle} color={stats.empty_clusters > 0 ? '#ef4444' : '#64748b'} />
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="cm-toolbar">
        <div className="cm-toolbar-left">
          <h6 className="cm-title">
            <FaProjectDiagram className="me-2" />
            Clusters
            <span className="cm-count">
              {searchResults !== null ? searchResults.length : pagination.total_count || clusters.length}
            </span>
          </h6>
        </div>

        <div className="cm-toolbar-center">
          <div className="cm-search-wrapper">
            <FaSearch className="cm-search-icon" />
            <Form.Control
              size="sm"
              placeholder="Search clusters…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="cm-search-input"
            />
            {searchTerm && (
              <button className="cm-search-clear" onClick={clearSearch}><FaTimes /></button>
            )}
            {searching && <Spinner size="sm" variant="info" className="cm-search-spinner" />}
          </div>
        </div>

        <div className="cm-toolbar-right">
          <OverlayTrigger placement="top" overlay={<Tooltip>Filters</Tooltip>}>
            <Button
              variant={showFilters ? 'info' : 'outline-secondary'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="cm-tool-btn"
            >
              <FaFilter />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger placement="top" overlay={<Tooltip>Compare clusters</Tooltip>}>
            <Button
              variant={compareMode ? 'warning' : 'outline-secondary'}
              size="sm"
              onClick={() => {
                setCompareMode(!compareMode);
                if (compareMode) { setCompareIds([]); }
              }}
              className="cm-tool-btn"
            >
              <FaExchangeAlt />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger placement="top" overlay={<Tooltip>Hierarchy tree</Tooltip>}>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={loadTree}
              disabled={treeLoading}
              className="cm-tool-btn"
            >
              {treeLoading ? <Spinner size="sm" /> : <FaSitemap />}
            </Button>
          </OverlayTrigger>

          <ButtonGroup size="sm">
            <Button
              variant={displayType === 'grid' ? 'primary' : 'outline-secondary'}
              onClick={() => setDisplayType('grid')}
              className="cm-tool-btn"
            >
              <FaThLarge />
            </Button>
            <Button
              variant={displayType === 'list' ? 'primary' : 'outline-secondary'}
              onClick={() => setDisplayType('list')}
              className="cm-tool-btn"
            >
              <FaList />
            </Button>
          </ButtonGroup>

          <Button variant="outline-secondary" size="sm" onClick={() => { fetchClusters(); fetchStats(); }} className="cm-tool-btn">
            <FaSyncAlt />
          </Button>
        </div>
      </div>

      {/* ── Compare bar ── */}
      {compareMode && (
        <div className="cm-compare-bar">
          <span className="cm-compare-info">
            <FaExchangeAlt className="me-2" />
            Select 2-10 clusters to compare. <strong>{compareIds.length}</strong> selected.
          </span>
          <div className="cm-compare-actions">
            <Button
              size="sm"
              variant="warning"
              onClick={runCompare}
              disabled={compareIds.length < 2 || compareLoading}
            >
              {compareLoading ? <Spinner size="sm" /> : 'Compare'}
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => { setCompareMode(false); setCompareIds([]); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* ── Filters Panel ── */}
      {showFilters && (
        <div className="cm-filters-panel">
          <Row className="g-2">
            <Col xs={6} md={2}>
              <Form.Label className="cm-filter-label">Sort By</Form.Label>
              <Form.Select size="sm" className="cm-filter-input" value={filters.sort_by} onChange={(e) => updateFilter('sort_by', e.target.value)}>
                <option value="member_count">Members</option>
                <option value="coherence">Coherence</option>
                <option value="stability">Stability</option>
                <option value="quality">Quality</option>
                <option value="created">Created</option>
                <option value="updated">Updated</option>
                <option value="evolution_count">Evolutions</option>
                <option value="label">Label</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="cm-filter-label">Direction</Form.Label>
              <Form.Select size="sm" className="cm-filter-input" value={filters.sort_direction} onChange={(e) => updateFilter('sort_direction', e.target.value)}>
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="cm-filter-label">Min Members</Form.Label>
              <Form.Control size="sm" type="number" min="0" placeholder="Any" className="cm-filter-input" value={filters.min_member_count} onChange={(e) => updateFilter('min_member_count', e.target.value)} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="cm-filter-label">Min Coherence</Form.Label>
              <Form.Control size="sm" type="number" min="0" max="1" step="0.1" placeholder="Any" className="cm-filter-input" value={filters.min_coherence_score} onChange={(e) => updateFilter('min_coherence_score', e.target.value)} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="cm-filter-label">Category</Form.Label>
              <Form.Select size="sm" className="cm-filter-input" value={filters.cluster_category} onChange={(e) => updateFilter('cluster_category', e.target.value)}>
                <option value="">All</option>
                {categories.map(c => (
                  <option key={c.category} value={c.category}>{c.category} ({c.cluster_count})</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="cm-filter-label">Per Page</Form.Label>
              <Form.Select size="sm" className="cm-filter-input" value={filters.page_size} onChange={(e) => updateFilter('page_size', parseInt(e.target.value))}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Col>
          </Row>
        </div>
      )}

      {/* ── Category Pills ── */}
      {categories.length > 1 && (
        <div className="cm-categories-row">
          <CategoryPill
            cat={{ category: 'All', cluster_count: pagination.total_count || clusters.length }}
            active={filters.cluster_category === ''}
            onClick={() => updateFilter('cluster_category', '')}
          />
          {categories.map(cat => (
            <CategoryPill
              key={cat.category}
              cat={cat}
              active={filters.cluster_category === cat.category}
              onClick={() => updateFilter('cluster_category', cat.category === filters.cluster_category ? '' : cat.category)}
            />
          ))}
        </div>
      )}

      {/* ── Search Results Banner ── */}
      {searchResults !== null && (
        <Alert variant="info" className="cm-alert" dismissible onClose={clearSearch}>
          Found <strong>{searchResults.length}</strong> clusters matching "<em>{searchTerm}</em>"
        </Alert>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className="cm-loading">
          <Spinner animation="border" variant="info" />
          <span>Loading clusters…</span>
        </div>
      ) : displayedClusters.length === 0 ? (
        <div className="cm-empty">
          <FaProjectDiagram className="cm-empty-icon" />
          <p>No clusters found</p>
          <span className="cm-empty-hint">Try adjusting filters or running a cluster analysis</span>
        </div>
      ) : (
        <>
          {displayType === 'grid' ? (
            <Row className="g-3">
              {displayedClusters.map(cluster => (
                <Col xs={12} sm={6} md={4} lg={3} key={cluster.cluster_id}>
                  <ClusterGridCard
                    cluster={cluster}
                    onSelect={selectCluster}
                    onCompare={toggleCompareId}
                    compareMode={compareMode}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <div className="cm-list-container">
              {displayedClusters.map(cluster => (
                <ClusterListRow
                  key={cluster.cluster_id}
                  cluster={cluster}
                  onSelect={selectCluster}
                  onCompare={toggleCompareId}
                  compareMode={compareMode}
                />
              ))}
            </div>
          )}

          {/* ── Pagination ── */}
          {pagination.total_pages > 1 && searchResults === null && (
            <div className="cm-pagination">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={filters.page <= 1}
                onClick={() => changePage(filters.page - 1)}
              >
                ← Prev
              </Button>
              <span className="cm-page-info">
                Page {filters.page} of {pagination.total_pages} ({pagination.total_count} total)
              </span>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={filters.page >= pagination.total_pages}
                onClick={() => changePage(filters.page + 1)}
              >
                Next →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClusterManager;
